import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { register, checkCompanyDomain, requestCompanyAccess } from '../services/authService';

const CompanyRegistration = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    username: '',
    email: '',
    full_name: '',
    date_of_birth: '',
    password: '',
    confirm_password: '',
    company_values: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [domainCheckResult, setDomainCheckResult] = useState(null);
  const [showJoinOption, setShowJoinOption] = useState(false);
  const [requestAccessSuccess, setRequestAccessSuccess] = useState(false);
  const emailCheckTimeout = useRef(null);
  const navigate = useNavigate();

  const checkEmailDomain = async (email) => {
    if (!email || !email.includes('@')) return;

    try {
      const result = await checkCompanyDomain(email);
      setDomainCheckResult(result);

      if (result.exists && !result.is_public_domain) {
        setShowJoinOption(true);
      } else {
        setShowJoinOption(false);
      }
    } catch (error) {
      console.error('Domain check failed:', error);
    }
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Debounce domain check
    if (name === 'email') {
      if (emailCheckTimeout.current) {
        clearTimeout(emailCheckTimeout.current);
      }
      emailCheckTimeout.current = setTimeout(() => checkEmailDomain(value), 500);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleRequestAccess = async () => {
    try {
      await requestCompanyAccess({
        email: formData.email,
        full_name: formData.full_name,
        company_id: domainCheckResult.company.id,
        message: `I would like to join ${domainCheckResult.company.name}`
      });

      setRequestAccessSuccess(true);
    } catch (error) {
      console.error('Access request failed:', error);
      setError('Failed to send access request. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!formData.date_of_birth) {
      setError('Date of birth is required');
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      // Parse company values from comma-separated string
      const companyValuesArray = formData.company_values
        ? formData.company_values
            .split(',')
            .map(v => v.trim())
            .filter(v => v.length > 0)
        : [];

      // Convert date from mm/dd/yyyy to YYYY-MM-DD
      let dateOfBirth = null;
      if (formData.date_of_birth) {
        const [month, day, year] = formData.date_of_birth.split('/');
        dateOfBirth = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      // Prepare data for API
      const registrationData = {
        username: formData.username,
        email: formData.email,
        full_name: formData.full_name,
        date_of_birth: dateOfBirth,
        password: formData.password,
        company_name: formData.company_name,
        plan: 'free',
        company_values: companyValuesArray.length > 0 ? companyValuesArray : undefined
      };

      await register(registrationData);

      // Show success message with email
      setRegisteredEmail(formData.email);
    } catch (error) {
      console.error('Registration error:', error);
      setError(
        error.response?.data?.detail ||
        'Registration failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // If access request successful, show success message
  if (requestAccessSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-green-100 mb-6">
              <svg className="h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
              Access Request Sent!
            </h2>
            <div className="bg-white shadow-md rounded-lg px-8 py-6 mb-6">
              <p className="text-lg text-gray-700 mb-4">
                Your request to join <strong>{domainCheckResult?.company?.name}</strong> has been sent to the administrators.
              </p>
              <p className="text-gray-600 mb-4">
                You will receive an email invitation once your request is approved.
              </p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If registration successful, show success message
  if (registeredEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-green-100 mb-6">
              <svg className="h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
              Registration Successful!
            </h2>
            <div className="bg-white shadow-md rounded-lg px-8 py-6 mb-6">
              <p className="text-lg text-gray-700 mb-4">
                A verification email has been sent to:
              </p>
              <p className="text-xl font-semibold text-blue-600 mb-4">
                {registeredEmail}
              </p>
              <p className="text-gray-600 mb-2">
                Please check your inbox and click the verification link to activate your account.
              </p>
              <p className="text-sm text-gray-500">
                Don't forget to check your spam folder if you don't see the email.
              </p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Register Your Company
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Start recognizing and appreciating your team
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {showJoinOption && domainCheckResult?.exists && (
          <div className="rounded-md bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  Company Already Exists
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    A company with the email domain <strong>@{domainCheckResult.domain}</strong> already exists: <strong>{domainCheckResult.company.name}</strong>
                  </p>
                  <p className="mt-2">
                    Would you like to request access to this company instead of creating a new one?
                  </p>
                </div>
                <div className="mt-4 flex space-x-3">
                  <button
                    type="button"
                    onClick={handleRequestAccess}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    Request Access
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowJoinOption(false)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Continue Registration Anyway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Company Information</h3>

              <div className="mb-4">
                <label htmlFor="company_name" className="block text-gray-700 text-sm font-bold mb-2">
                  Company Name *
                </label>
                <input
                  id="company_name"
                  name="company_name"
                  type="text"
                  required
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Acme Corporation"
                  value={formData.company_name}
                  onChange={handleChange}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="company_values" className="block text-gray-700 text-sm font-bold mb-2">
                  Company Values (Optional)
                </label>
                <input
                  id="company_values"
                  name="company_values"
                  type="text"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Innovation, Teamwork, Integrity"
                  value={formData.company_values}
                  onChange={handleChange}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter values that represent your company culture, separated by commas. For multi-word values, use PascalCase e.g. MoveFast, CustomerObsession
                </p>
              </div>
            </div>

            <div className="mb-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Admin Account</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="username" className="block text-gray-700 text-sm font-bold mb-2">
                    Username *
                  </label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="admin"
                    value={formData.username}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-2">
                    Email *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="admin@company.com"
                    value={formData.email}
                    onChange={handleEmailChange}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="full_name" className="block text-gray-700 text-sm font-bold mb-2">
                  Full Name *
                </label>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  required
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="John Doe"
                  value={formData.full_name}
                  onChange={handleChange}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="date_of_birth" className="block text-gray-700 text-sm font-bold mb-2">
                  Date of Birth *
                </label>
                <input
                  id="date_of_birth"
                  name="date_of_birth"
                  type="text"
                  required
                  placeholder="mm/dd/yyyy"
                  pattern="\d{2}/\d{2}/\d{4}"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="password" className="block text-gray-700 text-sm font-bold mb-2">
                    Password *
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    minLength="8"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="confirm_password" className="block text-gray-700 text-sm font-bold mb-2">
                    Confirm Password *
                  </label>
                  <input
                    id="confirm_password"
                    name="confirm_password"
                    type="password"
                    required
                    minLength="8"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                    value={formData.confirm_password}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-blue-600 hover:text-blue-800 font-medium text-sm"
            >
              Already have an account? Login
            </button>

            <button
              type="submit"
              disabled={loading}
              className={`inline-flex items-center py-2 px-6 border border-transparent text-sm font-medium rounded-md text-white ${
                loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              {loading && (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {loading ? 'Registering...' : 'Register Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyRegistration;
